/**
 * Build a 20-shade `HubBrandPalette` from a (primary, accent) brand colour
 * pair, per §4.1 of the Advanced Theming v2 design.
 *
 * Implementation note: this is a thin wrapper around the existing CSM colour
 * theme generator (which is also what `setHubBrandingStyle` already uses).
 * That generator runs the same Material-HCT-based 10-step ramp algorithm as
 * `@atlaskit/tokens`'s internal `generateColors`, but is exposed via a public
 * entry-point and is already a hubs dependency, so we don't have to duplicate
 * the colour science.
 *
 * Pure and synchronous — safe to call during render, including SSR.
 */
import { generateThemeBranding } from '@atlassian/csm-colour-theme-generator/generate-theme-branding';

import type { HubBrandPalette, HubBrandPaletteInputs, PaletteFamily, PaletteRef } from './types';

const SHADES = [
	['100', 'color100'],
	['200', 'color200'],
	['300', 'color300'],
	['400', 'color400'],
	['500', 'color500'],
	['600', 'color600'],
	['700', 'color700'],
	['800', 'color800'],
	['900', 'color900'],
	['1000', 'color1000'],
] as const;

const buildFamily = (family: PaletteFamily, hex: string): Partial<HubBrandPalette> => {
	const ramp = generateThemeBranding(hex);
	const out: Partial<HubBrandPalette> = {};
	for (const [shade, rampKey] of SHADES) {
		const key = `${family}.${shade}` as PaletteRef;
		out[key] = ramp[rampKey];
	}
	return out;
};

export const generateHubBrandPalette = ({
	primary,
	accent,
}: HubBrandPaletteInputs): HubBrandPalette => {
	return {
		...buildFamily('primary', primary),
		...buildFamily('accent', accent),
	} as HubBrandPalette;
};
